import { pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getRateBusinessSegmentId } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getServiceDefaultBusinessSegmentId } from '@mews-commander/legacy-entities/src/ducks/services';
import NewReservationScreenStep from '../enums/NewReservationScreenStep';
import {
    hasAnyReservation,
    getCurrentStep,
    getReservation,
    getServiceId,
    getReservationFromOrder,
    getSelectedServiceCategoryAvailability,
    getFilterValues,
    getFloorNumbersByResourceCategoryIds,
    getBusinessSegmentId,
    getRateOfferVoucherId,
} from '../selectors';
import { changeFilters, showRates } from './screens';
import { fetchRatePricing, fetchReservationPrice } from './api';
import { addInitialReservationProductCounts } from './basket';
import { RESERVATION_ADDED, RESERVATION_COUNT_CHANGED, RESERVATIONS_CLEARED, RESERVATION_CLEARED } from './types';

export const addReservation =
    (
        {
            rateId,
            categoryId,
            customPricePerNight = null,
            customPricePerFullTimeUnit = null,
            customRatePerNight = null,
            customRatePerFullTimeUnit = null,
            customPriceTotal = null,
            customPriceGrossTotal = null,
            customPriceTaxValues = null,
        }: $TSFixMe,
        scope: string
    ): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const serviceId = getServiceId(state, { scope });
        const optionsSegmentId = getBusinessSegmentId(state, { scope });
        const rateSegmentId = getRateBusinessSegmentId(state, { rateId });
        const defaultSegmentId = getServiceDefaultBusinessSegmentId(state, { id: serviceId });
        // @ts-expect-error
        const rateVoucherId = getRateOfferVoucherId(state, { rateId, categoryId, scope });

        const businessSegmentId = optionsSegmentId || rateSegmentId || defaultSegmentId || null;

        const reservationOrder = getReservationFromOrder(state, {
            serviceId,
            scope,
            rateId,
            businessSegmentId,
            categoryId,
            customPricePerNight,
            customRatePerNight,
            customRatePerFullTimeUnit,
            customNightCostToShow: customPricePerFullTimeUnit,
            customPriceTotal,
            customPriceGrossTotal,
            customPriceTaxValues,
        });

        const reservation = {
            ...reservationOrder,
            voucherId: rateVoucherId,
        };

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; categoryId: any; }' is not assignable to parameter of type '{ categoryId: string; }'.
        const { selectedCount } = getSelectedServiceCategoryAvailability(state, { scope, categoryId });
        const { selectedResourceId: requestedResourceId, selectedFloorNumbers } = getFilterValues(state, { scope });

        let requestedFloorNumbers;

        if (!requestedResourceId) {
            const resourceCategoryFloorNumbers = getFloorNumbersByResourceCategoryIds(state, {
                serviceId,
                resourceCategoryIds: [categoryId],
            });
            requestedFloorNumbers = pipe(
                selectedFloorNumbers,
                array.intersection(string.Eq)(resourceCategoryFloorNumbers)
            );
        }

        dispatch(
            createAction(
                RESERVATION_ADDED,
                { serviceId, reservation, count: selectedCount, requestedResourceId, requestedFloorNumbers },
                { scope }
            )
        );
        dispatch(changeFilters({ scope }));

        const { identifier } = reservation;
        dispatch(addInitialReservationProductCounts(identifier, scope, serviceId));
        return dispatch(fetchReservationPrice({ reservationKey: identifier, serviceId, scope }));
    };

export const changeReservationCount =
    (
        reservationKey: string,
        selectedCount: number,
        reservationCategoryCount: number,
        scope: string
    ): CommanderThunkAction =>
    (dispatch, getState) => {
        const meta = { scope };
        const countDifference = selectedCount - reservationCategoryCount;

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; reservationKey: any; }' is not assignable to parameter of type '{ reservationKey: string...
        const reservation = getReservation(getState(), { scope, reservationKey });
        const { serviceId } = reservation;

        dispatch(
            createAction(
                RESERVATION_COUNT_CHANGED,
                { reservationKey, count: countDifference, reservation, serviceId },
                meta
            )
        );
    };

export const clearReservation =
    (reservationKey: string, reservationCategoryCount: number, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const meta = { scope };

        // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; reservationKey: any; }' is not assignable to parameter of type '{ reservationKey: string...
        const reservation = getReservation(getState(), { scope, reservationKey });
        const { serviceId } = reservation;

        dispatch(
            createAction(
                RESERVATION_CLEARED,
                { reservationKey, count: reservationCategoryCount, reservation, serviceId },
                meta
            )
        );
    };

export const clearReservations =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const props = { scope };
        const state = getState();

        if (hasAnyReservation(state, props)) {
            if (getCurrentStep(state, props) === NewReservationScreenStep.Summary) {
                dispatch(fetchRatePricing({ scope, origin: 'NRS-clearReservations' }));
                dispatch(showRates(scope));
            }

            dispatch(createAction(RESERVATIONS_CLEARED, undefined, props));
        }
    };
